import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { DownloadSimple, X } from "@phosphor-icons/react";

export const InstallPWA = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    // Check if already installed
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches;
    if (isStandalone) return;

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // For non-iOS, listen for install prompt
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setIsVisible(true);
    };

    window.addEventListener("beforeinstallprompt", handler);

    // Show iOS instructions after a delay
    if (iOS) {
      const timer = setTimeout(() => setIsVisible(true), 3000);
      return () => clearTimeout(timer);
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;

    const result = await installPrompt.prompt();
    console.log("Install prompt result:", result);

    setInstallPrompt(null);
    setIsVisible(false);
  };

  const handleDismiss = () => {
    setIsVisible(false);
    // Don't show again for this session
    sessionStorage.setItem("pwa-dismissed", "true");
  };

  // Check if dismissed this session
  useEffect(() => {
    if (sessionStorage.getItem("pwa-dismissed")) {
      setIsVisible(false);
    }
  }, []);

  if (!isVisible) return null;

  return (
    <div
      className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 bg-[#111111] border border-white/20 rounded-sm p-4 shadow-lg z-50"
      data-testid="pwa-install-prompt"
    >
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 text-white/40 hover:text-white"
        aria-label="Dismiss"
      >
        <X weight="bold" className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-3">
        <div className="p-2 rounded-sm bg-[#007AFF]/20">
          <DownloadSimple weight="duotone" className="w-6 h-6 text-[#007AFF]" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-sm font-bold uppercase tracking-tight text-white">
            Install App
          </h3>
          {isIOS ? (
            <div>
              <p className="text-xs text-white/60 mt-1">
                Tap <span className="text-[#007AFF]">Share</span> then{" "}
                <span className="text-[#007AFF]">"Add to Home Screen"</span>
              </p>
              <button
                onClick={() => setShowIOSInstructions(!showIOSInstructions)}
                className="text-xs text-[#007AFF] mt-2 underline"
              >
                {showIOSInstructions ? "Hide" : "Show"} instructions
              </button>
              {showIOSInstructions && (
                <ol className="text-xs text-white/60 mt-2 space-y-1 list-decimal list-inside">
                  <li>Tap the Share button in Safari</li>
                  <li>Scroll down and tap "Add to Home Screen"</li>
                  <li>Tap "Add" to confirm</li>
                </ol>
              )}
            </div>
          ) : (
            <>
              <p className="text-xs text-white/60 mt-1">
                Add Flight Logbook to your home screen for quick access
              </p>
              <Button
                onClick={handleInstall}
                size="sm"
                className="mt-3 bg-[#007AFF] hover:bg-[#005bb5] text-white text-xs rounded-sm"
                data-testid="pwa-install-button"
              >
                Install Now
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstallPWA;
